import {Check, CircleNotch, Target} from '@phosphor-icons/react';
import cloneDeep from 'lodash.clonedeep';
import {bool, func, object, oneOf} from 'prop-types';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {twMerge} from 'tailwind-merge';

import {isNum} from '../../../../lib/math.mjs';
import {
  EXERCISE_TYPE,
  STATUSES,
  STATUSES_FINISHED,
  WEIGHT_INCREMENTS,
} from '../../../../lib/training.mjs';
import {useMesocycle, useUpdateDay, useUpdateRepsActual} from '../api.js';
import {useConfirmation} from '../ConfirmationContext.jsx';
import {DIALOG_CONFIGS, LS_KEY_WORKOUT_TUTORIAL_SEEN} from '../constants.js';
import {useExercisesById, useMutationCallback} from '../utils/hooks.js';
import {
  defaultRepsIncrements,
  getPickerIncrements,
  getSetWeightPickerVal,
  getWeightActualFromPickerVal,
} from '../utils/index.js';
import {runAfterAnimations} from '../utils/misc.js';
import storage from '../utils/storage.js';
import useLocalStore from '../utils/useLocalStore.js';
import Ping from './ui/Ping.jsx';
import TargetSelect from './ui/TargetSelect.jsx';

// set comes in multiple states:
// 1. Done: user has finished the set, and there are no local changes to completed set's data
// 2. Active: this is the "next set in line" AND the set is dirty
// 3. Inactive: user has not finished the set yet AND is it not "next up in line"
// 4. Saving: pressed save. save in flight.

const BUTTON_TYPE = {
  done: 'done',
  active: 'active',
  inactive: 'inactive',
  spinning: 'spinning',
};

const DISABLED_BY_BUTTON_TYPE = {
  [BUTTON_TYPE.done]: true,
  [BUTTON_TYPE.active]: false,
  [BUTTON_TYPE.inactive]: true,
  [BUTTON_TYPE.spinning]: true,
};

const buttonStylesBase = 'btn btn-sm flex items-center justify-center';
const BUTTON_STYLES_BY_TYPE = {
  [BUTTON_TYPE.done]: 'btn-success !btn-ghost',
  [BUTTON_TYPE.active]: 'btn-accent',
  [BUTTON_TYPE.inactive]: 'btn-disabled',
  [BUTTON_TYPE.spinning]: 'btn-square min-w-[82px]',
};

function getButtonType(setStatus, isSetFirstUnfinished, isSaving) {
  if (isSaving) {
    return BUTTON_TYPE.spinning;
  }

  if (STATUSES_FINISHED.set.includes(setStatus)) {
    return BUTTON_TYPE.done;
  }

  if ([STATUSES.set.pendingWeight, STATUSES.set.pendingReps].includes(setStatus)) {
    return BUTTON_TYPE.inactive;
  }

  if (STATUSES.set.ready === setStatus) {
    return isSetFirstUnfinished ? BUTTON_TYPE.active : BUTTON_TYPE.inactive;
  }
}

function SaveButton({type, onClick, showIndicator}) {
  const isSaving = useLocalStore(st => st.isSaving);
  const isDisabled = DISABLED_BY_BUTTON_TYPE[type] || isSaving;

  const buttonStylesForType = BUTTON_STYLES_BY_TYPE[type];

  let contents;
  switch (type) {
    case BUTTON_TYPE.active:
    case BUTTON_TYPE.inactive:
      contents = 'Log set';
      break;
    case BUTTON_TYPE.spinning:
      contents = null;
      break;
    case BUTTON_TYPE.done:
      contents = <Check size={20} weight="bold" className="!text-success-content" />;
      break;
    // No default
  }

  return (
    <div className="relative flex justify-end">
      <button
        type="button"
        disabled={isDisabled}
        className={twMerge(buttonStylesBase, buttonStylesForType)}
        onClick={onClick}
      >
        {type === BUTTON_TYPE.spinning && <span className="loading"></span>}
        {contents}
      </button>
      {showIndicator && <Ping />}
    </div>
  );
}

SaveButton.propTypes = {
  type: oneOf(Object.values(BUTTON_TYPE)).isRequired,
  onClick: func.isRequired,
  showIndicator: bool.isRequired,
};

export default function MesoDayExerciseSet({set, dayExercise, day, showTutorial}) {
  const bodyweight = set.bodyweight;
  const {position, weightTarget, weightActual, repsTarget, repsActual} = set;

  const {mesoKey} = useParams();
  const exercisesById = useExercisesById();
  const {confirmation} = useConfirmation();
  const {data: meso} = useMesocycle(mesoKey);
  const setIsSaving = useLocalStore(st => st.setIsSaving);

  const weightValue = isNum(weightActual)
    ? weightActual
    : isNum(weightTarget)
    ? weightTarget
    : null;

  const [bounce, setBounce] = useState(false);
  const [localWeightValue, setLocalWeightValue] = useState(weightValue);

  const updateRepsActual = useUpdateRepsActual(mesoKey, set.id); // used for spin on reps selector

  const saveWeight = useUpdateDay(day.id, {
    meta: {action: 'saveWeight'},
  });
  const saveSet = useUpdateDay(day.id, {
    meta: {action: 'saveSet'},
    onSuccess: () => {
      // Once a user saves a set, we know they've seen the "log set" and "enter reps" tutorials
      if (!storage.getItem(LS_KEY_WORKOUT_TUTORIAL_SEEN)) {
        storage.setItem(LS_KEY_WORKOUT_TUTORIAL_SEEN, true);
      }
    },
    onSettled: () => {
      runAfterAnimations(() => setIsSaving(false));
    },
  });

  const exerciseType = exercisesById[dayExercise.exerciseId].exerciseType;
  const pickerVal = getSetWeightPickerVal(exerciseType, weightValue, bodyweight);
  const pickerValTarget = getSetWeightPickerVal(exerciseType, weightTarget, bodyweight);
  const pickerIncrements = getPickerIncrements(
    WEIGHT_INCREMENTS,
    exerciseType,
    meso.unit,
    bodyweight
  );

  const repsValue = isNum(repsActual) ? repsActual : repsTarget <= 0 ? '' : repsTarget;

  // show the icon if the number in the dropdown matches the reps target which is a positive integer
  const showRepsTarget = repsTarget > 0 && repsValue === repsTarget;

  const showWeightTarget =
    (!isNum(pickerVal) && pickerValTarget) || (pickerVal !== '' && pickerVal === pickerValTarget);

  const repsPlaceholder = repsActual === -1 ? 'n/a' : 'ENTER';

  const bodyweightOnly = exerciseType === EXERCISE_TYPE.bodyweightOnly;

  // Is this set the first one that doesn't have a finishedAt?
  const isSetFirstUnfinished = dayExercise.sets.find(s => !s.finishedAt)?.id === set.id;

  // Start bouncing if the weight value was updated via a different set
  useEffect(() => {
    if (localWeightValue !== weightValue) {
      if (!bounce && showRepsTarget && repsTarget > 0) {
        setBounce(true);
      }
      setLocalWeightValue(weightValue);
    }
  }, [bounce, localWeightValue, repsTarget, showRepsTarget, weightValue]);

  // Cancel the bounce
  useEffect(() => {
    if (bounce) {
      const timer = setTimeout(() => setBounce(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [bounce]);

  const {showWeightIndicator, showRepsIndicator, showSaveIndicator} = useMemo(() => {
    const response = {
      showWeightIndicator: false,
      showRepsIndicator: false,
      showSaveIndicator: false,
    };

    if (showTutorial && set.position === 0) {
      if (set.status === STATUSES.set.ready) {
        response.showSaveIndicator = true;
      } else if (set.status === STATUSES.set.pendingReps) {
        response.showRepsIndicator = true;
      } else if (set.status === STATUSES.set.pendingWeight) {
        response.showWeightIndicator = true;
      }
    }
    return response;
  }, [set.position, set.status, showTutorial]);

  const handleSaveWeight = useMutationCallback(
    async value => {
      if (STATUSES_FINISHED.day.includes(day.status)) {
        const shouldContinue = await confirmation(DIALOG_CONFIGS.dayCompleteWarning);
        if (!shouldContinue) {
          return;
        }
      }

      const newExercises = cloneDeep(day.exercises);
      const floatValue = parseFloat(value);
      const newWeightActual = getWeightActualFromPickerVal(floatValue, exerciseType, bodyweight);

      for (const set of newExercises[dayExercise.position].sets) {
        set.weightActual = newWeightActual;
      }

      saveWeight.mutate(
        {
          ...day,
          exercises: newExercises,
        },
        {
          onSuccess: () => {
            if (showRepsTarget && repsTarget > 0) {
              setBounce(true);
            }
          },
        }
      );
    },
    [
      bodyweight,
      confirmation,
      day,
      dayExercise.position,
      exerciseType,
      repsTarget,
      saveWeight,
      showRepsTarget,
    ]
  );

  const handleSaveReps = useMutationCallback(
    async value => {
      if (STATUSES_FINISHED.day.includes(day.status)) {
        const shouldContinue = await confirmation(DIALOG_CONFIGS.dayCompleteWarning);
        if (!shouldContinue) {
          return;
        }
      }

      const newRepsActual = parseInt(value);
      updateRepsActual.mutate({reps: newRepsActual});
    },
    [confirmation, day.status, updateRepsActual]
  );

  const handleSaveSet = useCallback(async () => {
    if (STATUSES_FINISHED.day.includes(day.status)) {
      const shouldContinue = await confirmation(DIALOG_CONFIGS.dayCompleteWarning);
      if (!shouldContinue) {
        return;
      }
    }

    const newMuscleGroups = cloneDeep(day.muscleGroups);
    const exerciseMuscleGroup = newMuscleGroups.find(
      mg => mg.muscleGroupId === dayExercise.muscleGroupId
    );

    if (!day.finishedAt) {
      // When saving any value to a set, we need to ask
      // the user for an updated muscle group pump and workload ratings
      exerciseMuscleGroup.pump = null;
      exerciseMuscleGroup.workload = null;
    }

    const newExercises = cloneDeep(day.exercises);

    const set = newExercises[dayExercise.position].sets[position];
    const reps = isNum(repsActual)
      ? repsActual
      : isNum(repsTarget) && repsTarget > 0
      ? repsTarget
      : null;

    // if user has not provided it, set weightActual to whatever we have for weightTarget
    for (const set of newExercises[dayExercise.position].sets) {
      set.weightActual = set.weightActual || set.weightTarget;
    }

    if (!set.weightActual) {
      const err = new Error('trying to save set without weight');
      err.extraData = {set: JSON.parse(JSON.stringify(set))};
      throw err;
    }

    if (!isNum(reps)) {
      const err = new Error('trying to save set without reps');
      err.extraData = {set: JSON.parse(JSON.stringify(set))};
      throw err;
    }

    set.repsActual = reps;
    set.finishedAt = set.finishedAt || new Date().toISOString();

    setIsSaving(true);
    saveSet.mutate({
      ...day,
      exercises: newExercises,
      muscleGroups: newMuscleGroups,
    });
  }, [
    confirmation,
    day,
    dayExercise.muscleGroupId,
    dayExercise.position,
    position,
    repsActual,
    repsTarget,
    saveSet,
    setIsSaving,
  ]);

  const saveButtonType = useMemo(
    () => getButtonType(set.status, isSetFirstUnfinished, saveSet.isWorking),
    [isSetFirstUnfinished, set.status, saveSet.isWorking]
  );

  return (
    <>
      {/* set col */}
      <div className="hidden items-center justify-center p-2 text-center font-mono text-sm desktop:col-span-1 desktop:flex">
        {set.position + 1}
      </div>
      {/* weight col */}
      <div className="col-span-1 p-2">
        {set.position === 0 && !bodyweightOnly ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="relative">
              <TargetSelect
                value={pickerVal}
                target={pickerValTarget}
                options={pickerIncrements}
                onChange={handleSaveWeight}
                isActive={isSetFirstUnfinished && set.status === STATUSES.set.pendingWeight}
                isLoading={saveWeight.isWorking}
                showIndicator={showWeightIndicator}
              />
              {showWeightTarget && (
                <div className="absolute -left-2.5 -top-2 rounded-full bg-base-100">
                  <Target className="h-5 w-5" />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mr-4 flex h-full items-center justify-center font-mono text-sm font-medium">
            <span className="">
              {saveWeight.isWorking ? (
                <CircleNotch size={18} className="animate-spin text-gray-400" />
              ) : bodyweightOnly ? (
                set.bodyweight
              ) : (
                pickerVal
              )}
            </span>
          </div>
        )}
      </div>
      {/* reps rir col  */}
      <div className="col-span-2 flex justify-center">
        {/* rep col  */}
        <div className="py-2">
          <div className="flex items-center">
            <div className="relative">
              <TargetSelect
                placeholder={repsPlaceholder}
                value={repsValue}
                target={repsTarget}
                prevTarget={dayExercise.sets[set.position - 1]?.repsActual}
                options={defaultRepsIncrements}
                onChange={handleSaveReps}
                isActive={isSetFirstUnfinished && set.status === STATUSES.set.pendingReps}
                isLoading={saveWeight.isWorking || updateRepsActual.isWorking}
                showIndicator={showRepsIndicator}
              />
              {showRepsTarget && (
                <div
                  className={twMerge(
                    'absolute -top-2 -left-2.5 bg-base-100 rounded-full',
                    bounce ? 'animate-bounce' : ''
                  )}
                >
                  <Target className="h-5 w-5" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* action col  */}
      <div className="col-span-1 flex items-center justify-end p-2">
        <SaveButton
          type={saveButtonType}
          onClick={handleSaveSet}
          showIndicator={showSaveIndicator}
        />
      </div>
    </>
  );
}

MesoDayExerciseSet.propTypes = {
  set: object.isRequired,
  dayExercise: object.isRequired,
  day: object.isRequired,
  showTutorial: bool.isRequired,
};
